import { AxiosResponse } from 'axios';
import { SET_EMPLOYEE_POSITIONS, SET_EMPLOYEE_POSITION, SET_ERRORMSG } from '@/store/mutation_types';
import {
  CLEAR_EMPLOYEE_POSITION_ISLOADING,
  DELETE_EMPLOYEE_POSITION,
  GET_UNASSIGNED_EMPLOYEES,
  EDIT_EMPLOYEE_POSITION,
  GET_EMPLOYEE_POSITIONS,
  GET_EMPLOYEE_POSITION,
  REGISTER_EMPLOYEE_POSITION,
} from '@/store/action_types';
import EmployeePosition from '@/services/employee_position';
import { Module } from 'vuex';
import { EditEmployeePosition, RegisterEmployeePosition, GetEmployeePosition } from '@/dtos/employee_position.dto';

/**
 * The axios interceptor pushes a human message into the error store for 4xx/5xx and
 * then resolves with `undefined`, so committing `result?.data?.msg` unconditionally
 * would overwrite that message with undefined.
 */
const reportError = (commit: any, result: any) => {
  const msg = result?.data?.msg;
  if (msg) commit(SET_ERRORMSG, msg);
};

interface Slot {
  data: any;
  isLoading: boolean;
  requestId: number;
}

const emptySlot = (): Slot => ({ data: null, isLoading: false, requestId: 0 });

const apply = (slot: Slot, payload: any) => {
  if (payload.requestId !== undefined && payload.requestId < slot.requestId) return;
  if (payload.requestId !== undefined) slot.requestId = payload.requestId;
  if ('data' in payload) slot.data = payload.data;
  slot.isLoading = Boolean(payload.isLoading);
};

const employeePosition: Module<any, {}> = {
  state: {
    employeePosition: emptySlot(),
    employeePositions: emptySlot(),
  },
  getters: {
    employeePosition: (state: any) => state.employeePosition,
    employeePositions: (state: any) => state.employeePositions,
  },
  mutations: {
    [SET_EMPLOYEE_POSITION]: (state: any, payload: any) => apply(state.employeePosition, payload),
    [SET_EMPLOYEE_POSITIONS]: (state: any, payload: any) => apply(state.employeePositions, payload),
  },
  actions: {
    /**
     * Every action is wrapped: the axios interceptor REJECTS on a network drop or
     * timeout, and an unwrapped `await` left the loading flag stuck on forever.
     */
    [GET_EMPLOYEE_POSITIONS]: async ({ commit, state }: any, payload: any) => {
      const requestId = state.employeePositions.requestId + 1;
      commit(SET_EMPLOYEE_POSITIONS, { data: null, isLoading: true, requestId });

      try {
        const result: AxiosResponse = await EmployeePosition.getEmployeePositions(payload as GetEmployeePosition);
        if (result?.data?.status) {
          commit(SET_EMPLOYEE_POSITIONS, { data: result.data.data ?? [], isLoading: false, requestId });
          return true;
        }
        commit(SET_EMPLOYEE_POSITIONS, { data: [], isLoading: false, requestId });
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_EMPLOYEE_POSITIONS, { data: [], isLoading: false, requestId });
        return false;
      }
    },

    [GET_EMPLOYEE_POSITION]: async ({ commit }: any, payload: any) => {
      commit(SET_EMPLOYEE_POSITION, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await EmployeePosition.getEmployeePositions(payload as GetEmployeePosition);
        if (result?.data?.status) {
          commit(SET_EMPLOYEE_POSITION, { data: result.data.data?.[0] ?? null, isLoading: false });
          return true;
        }
        commit(SET_EMPLOYEE_POSITION, { data: null, isLoading: false });
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_EMPLOYEE_POSITION, { data: null, isLoading: false });
        return false;
      }
    },

    [REGISTER_EMPLOYEE_POSITION]: async ({ commit }: any, payload: any) => {
      commit(SET_EMPLOYEE_POSITION, { isLoading: true });

      try {
        const result: AxiosResponse = await EmployeePosition.registerEmployeePosition(payload as RegisterEmployeePosition);
        commit(SET_EMPLOYEE_POSITION, { data: result?.data?.data ?? null, isLoading: false });
        if (result?.data?.status) return true;
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_EMPLOYEE_POSITION, { isLoading: false });
        return false;
      }
    },

    [EDIT_EMPLOYEE_POSITION]: async ({ commit }: any, payload: any) => {
      commit(SET_EMPLOYEE_POSITION, { isLoading: true });

      try {
        const result: AxiosResponse = await EmployeePosition.editEmployeePosition(payload as EditEmployeePosition);
        commit(SET_EMPLOYEE_POSITION, { data: result?.data?.data ?? null, isLoading: false });
        if (result?.data?.status) return true;
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_EMPLOYEE_POSITION, { isLoading: false });
        return false;
      }
    },

    /** Employees who hold no position at all. Admin-only on the server. */
    [GET_UNASSIGNED_EMPLOYEES]: async ({ commit }: any, payload: any) => {
      try {
        const result: AxiosResponse = await EmployeePosition.getUnassignedEmployees(payload);
        if (result?.data?.status) return result.data.data ?? [];
        reportError(commit, result);
        return [];
      } catch {
        return [];
      }
    },

    [DELETE_EMPLOYEE_POSITION]: async ({ commit }: any, payload: string) => {
      commit(SET_EMPLOYEE_POSITION, { isLoading: true });

      try {
        const result: AxiosResponse = await EmployeePosition.deleteEmployeePosition(payload);
        commit(SET_EMPLOYEE_POSITION, { isLoading: false });
        if (result?.data?.status) return true;
        reportError(commit, result);
        return false;
      } catch {
        commit(SET_EMPLOYEE_POSITION, { isLoading: false });
        return false;
      }
    },

    [CLEAR_EMPLOYEE_POSITION_ISLOADING]: ({ commit }: any) => {
      commit(SET_EMPLOYEE_POSITION, { isLoading: false });
      commit(SET_EMPLOYEE_POSITIONS, { isLoading: false });
    },
  },
};

export default employeePosition;
